// Свои данные для входа: имя и фраза.
//
// ## Почему диалог из полосы разделов, а не блок в «Настройках»
//
// «Настройки» открываются по праву `core.setting.view`, и у части ролей его нет. Сотрудник,
// которому двое главных выдали доступ в программу, не смог бы сменить себе фразу вовсе — она
// осталась бы навсегда той, что ему придумали при заведении, и знали бы её двое. А своё имя в
// нижней части полосы видно с любого экрана.
//
// ## Почему нынешняя фраза спрашивается всегда
//
// Даже когда меняют только имя. Компьютер финансового директора стоит в кабинете, и минута без
// присмотра при отсутствии этой проверки означает угон: подошедший меняет имя и фразу, хозяин
// записи не входит уже никогда, а в журнале остаётся его собственное имя.
//
// ## Что происходит после смены
//
// Сервер гасит ВСЕ входы человека на всех устройствах — в этом смысл смены фразы: её меняют в
// том числе потому, что её кто-то узнал. Свой вход возвращается тут же новой парой ключей,
// поэтому экран не моргает и заново вводить ничего не надо.

import { useState } from 'react';

import {
  RiErrorWarningLine,
  RiEyeLine,
  RiEyeOffLine,
  RiKey2Line,
  RiUser3Line,
} from '@remixicon/react';

import { СтрокаВерсии } from '@/layout/ЧтоНового';
import { changeMyCredentials } from '@/shared/api/endpoints/users';
import { АДРЕС_ПОЛИТИКИ } from '@/shared/auth/документы';
import { adoptSession } from '@/shared/auth/session';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

/** Столько же требует сервер. Названо здесь, чтобы человек узнал об этом ДО отправки. */
const МИНИМУМ_ФРАЗЫ = 12;

export function МойВходДиалог({
  открыт,
  закрыть,
  имяВхода,
  открытьИсторию,
  открытьЗакрытие,
}: {
  открыт: boolean;
  закрыть: () => void;
  имяВхода: string;
  /** Закрывает окно и открывает панель истории обновлений. Здесь строка версии видна всем
   *  ролям: «Настройки» открыты не каждому, а своё имя внизу полосы разделов — каждому. */
  открытьИсторию: () => void;
  /** Уводит в отдельное окно закрытия своей записи: два необратимых действия в одном окне
   *  спутались бы, а подтверждение у них разное. */
  открытьЗакрытие: () => void;
}) {
  const [логин, setЛогин] = useState(имяВхода);
  const [нынешняя, setНынешняя] = useState('');
  const [новая, setНовая] = useState('');
  const [повтор, setПовтор] = useState('');
  const [видно, setВидно] = useState(false);
  const [занято, setЗанято] = useState(false);
  const [ошибка, setОшибка] = useState<unknown>(null);
  const [заслон, setЗаслон] = useState<string | null>(null);
  const [готово, setГотово] = useState(false);

  function сбросить() {
    setЛогин(имяВхода);
    setНынешняя('');
    setНовая('');
    setПовтор('');
    setВидно(false);
    setОшибка(null);
    setЗаслон(null);
    setГотово(false);
  }

  /**
   * Почему нажатие не сработает — словами, а не серой кнопкой.
   *
   * Выключенная кнопка не доводит нажатие до обработчика, и со стороны это неотличимо от
   * сломанной программы (правило 10). Здесь это особенно легко: человек вписал новую фразу,
   * забыл нынешнюю — и видит серое «Сохранить» без единого слова о том, чего не хватает.
   */
  function чегоНеХватает(): { сообщение?: string; поле?: string } | null {
    if (нынешняя === '') {
      return {
        сообщение: 'Впишите нынешнюю фразу: без неё смена не пройдёт.',
        поле: 'мой-вход-нынешняя',
      };
    }
    if (!имяМеняется && !фразаМеняется) {
      return { сообщение: 'Пока менять нечего: впишите новое имя входа либо новую фразу.' };
    }
    // Дальше поле уже само подписано красным под собой. Второй раз то же самое словами под
    // кнопкой — это не забота, а рябь: человек читает одно и то же дважды и ищет разницу.
    // Вместо повтора ставим курсор туда, где проблема: подпись и так рядом.
    if (короткая) return { поле: 'мой-вход-новая' };
    if (фразаМеняется && повтор !== новая) return { поле: 'мой-вход-повтор' };
    return null;
  }

  const имяМеняется = логин.trim() !== '' && логин.trim() !== имяВхода;
  const фразаМеняется = новая !== '';
  const повторНеСошёлся = фразаМеняется && повтор !== '' && повтор !== новая;
  const короткая = фразаМеняется && новая.length < МИНИМУМ_ФРАЗЫ;

  function нажать() {
    const причина = чегоНеХватает();
    setЗаслон(причина?.сообщение ?? null);
    if (причина?.поле !== undefined) {
      document.getElementById(причина.поле)?.focus();
    }
    if (причина === null && !занято) void отправить();
  }

  async function отправить() {
    setЗанято(true);
    setОшибка(null);
    setЗаслон(null);
    try {
      const ответ = await changeMyCredentials({
        current_password: нынешняя,
        ...(имяМеняется ? { login: логин.trim() } : {}),
        ...(фразаМеняется ? { password: новая } : {}),
      });
      // Ключи меняем ДО того, как показать «готово»: иначе первый же следующий запрос уйдёт
      // с погашенным ключом и человек увидит экран входа вместо подтверждения.
      adoptSession(ответ);
      setГотово(true);
      setНынешняя('');
      setНовая('');
      setПовтор('');
    } catch (сбой) {
      setОшибка(сбой);
    } finally {
      setЗанято(false);
    }
  }

  return (
    <Modal.Root
      open={открыт}
      onOpenChange={(следующее) => {
        if (!следующее) {
          закрыть();
          сбросить();
        }
      }}
    >
      <Modal.Content>
        <Modal.Header
          icon={RiKey2Line}
          title='Мой вход'
          description='Имя и фраза, которыми вы открываете программу.'
        />
        <Modal.Body>
          {готово ? (
            <p className='text-paragraph-sm text-text-sub-600'>
              Готово. Если вы входили в программу где-то ещё — на другом компьютере или на
              телефоне, — там вход закрыт: войдите заново новыми данными. Здесь всё осталось
              открытым, заново вводить ничего не надо.
            </p>
          ) : (
            <form
              className='flex flex-col gap-3'
              noValidate
              onSubmit={(событие) => {
                событие.preventDefault();
                нажать();
              }}
            >
              <TextField
                id='мой-вход-логин'
                label='Имя входа'
                value={логин}
                onChange={setЛогин}
                icon={RiUser3Line}
                autoComplete='username'
                maxLength={128}
                hint='Оставьте как есть, если менять не нужно.'
              />
              <TextField
                id='мой-вход-нынешняя'
                label='Нынешняя фраза'
                required
                value={нынешняя}
                onChange={setНынешняя}
                type={видно ? 'text' : 'password'}
                autoComplete='current-password'
                maxLength={128}
                hint='Нужна всегда: так программа понимает, что за компьютером именно вы.'
              />
              <TextField
                id='мой-вход-новая'
                label='Новая фраза'
                value={новая}
                onChange={setНовая}
                type={видно ? 'text' : 'password'}
                autoComplete='new-password'
                maxLength={128}
                error={короткая ? `Не короче ${МИНИМУМ_ФРАЗЫ} знаков.` : undefined}
                hint={`Оставьте пустым, если меняете только имя. Не короче ${МИНИМУМ_ФРАЗЫ} знаков.`}
              />
              {фразаМеняется && (
                <TextField
                  id='мой-вход-повтор'
                  label='Новая фраза ещё раз'
                  required
                  value={повтор}
                  onChange={setПовтор}
                  type={видно ? 'text' : 'password'}
                  autoComplete='new-password'
                  maxLength={128}
                  error={повторНеСошёлся ? 'Не совпадает с новой фразой.' : undefined}
                  hint='Проверка от опечатки: фразу вводят вслепую, и опечатку видно только здесь.'
                />
              )}
              <button
                type='button'
                onClick={() => setВидно((было) => !было)}
                className='flex items-center gap-1.5 self-start text-paragraph-xs text-text-sub-600 transition duration-200 ease-out hover:text-text-strong-950'
              >
                {видно ? (
                  <RiEyeOffLine className='size-4' />
                ) : (
                  <RiEyeLine className='size-4' />
                )}
                {видно ? 'Скрыть фразы' : 'Показать фразы'}
              </button>
              {заслон !== null && (
                <Hint.Root hasError>
                  <Hint.Icon as={RiErrorWarningLine} />
                  {заслон}
                </Hint.Root>
              )}
              {ошибка !== null && <ErrorAlert error={ошибка} />}
            </form>
          )}
          {/* Своим доступом человек распоряжается там же, где своей фразой. Отдельной кнопкой
              в подвале эта строка быть не может: подвал занят «Сохранить», и красная кнопка
              рядом с ним превращает обычную смену фразы в минное поле. */}
          {!готово && (
            <div className='mt-4 flex flex-col items-start gap-2'>
              {/* Ссылка на политику обязана быть ВНУТРИ приложения, а не только в карточке
                  магазина: правило Apple 5.1.1(i) проверяют механически, и до 19.09.2026 её
                  не было нигде. Своим экраном её не показываем — вторая копия текста
                  разойдётся с сайтом в первый же день. */}
              <a
                href={АДРЕС_ПОЛИТИКИ}
                target='_blank'
                rel='noreferrer'
                className='text-paragraph-xs text-text-sub-600 underline decoration-dotted underline-offset-2 transition duration-200 ease-out hover:text-text-strong-950'
              >
                Как мы обращаемся с вашими данными
              </a>
              <button
                type='button'
                onClick={открытьЗакрытие}
                className='text-paragraph-xs text-text-sub-600 underline decoration-dotted underline-offset-2 transition duration-200 ease-out hover:text-error-base'
              >
                Закрыть свою учётную запись
              </button>
              {/* Номер программы здесь нужен по той же причине, что в «Настройках»: назвать его
                  в разговоре. А история обновлений — ответ на «что поменялось», который
                  финансовый директор просил держать рядом с номером. */}
              <СтрокаВерсии открыть={открытьИсторию} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              {готово ? 'Закрыть' : 'Отмена'}
            </Button.Root>
          </Modal.Close>
          {!готово && (
            <Button.Root
              variant='primary'
              mode='filled'
              size='small'
              className='w-full'
              type='button'
              onClick={нажать}
            >
              {занято ? 'Меняем…' : 'Сохранить'}
            </Button.Root>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
